// Sales "Quotation detail" (this task's brief) — a read-only document plus
// the actions a salesperson may take on it: unseal if permitted
// (can_unseal), re-quote if expired (can_requote), and add a note. Reached
// from "My quotations" (pages/MyQuotationsPage.jsx) and from
// pages/TodayPage.jsx's rejected/expiring rows — previously the only
// sales-facing unseal action lived inside the pricing workspace, which
// loses its own seal section once the quotation moves past 'draft' (see
// PROGRESS.md's sealed-bid-tender entry).
//
// Keep the read-only viewer read-only for the document itself; actions sit
// alongside it, not inside it (this task's brief) — same layout discipline
// as pages/AllProformasViewPage.jsx's own admin equivalent.
//
// Not the same component as components/SealSection.jsx: that one also
// shows "first opened at" via the document-access log
// (GET /admin/requests/:id/document-access-log), an admin-only endpoint a
// sales session would 403 against. This page's own inline seal block below
// shows everything getForSalesDetail's response already carries
// (sealed_at, seal_bid_opening_at, unsealed_at, unsealed_by_name,
// unseal_route) without that dependency.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiClient from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useSalesQuotationDetail,
  useUnsealSalesQuotation,
  useRequoteSalesQuotation,
  useAddRequestNote,
  useSalesQuotationPayment,
  useUpdateSalesPaymentLetter,
} from '../hooks/useSalesQuotations.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import PaymentLetterForm from '../components/PaymentLetterForm.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { ProformaDocument } from 'shared/components/ProformaDocument/index.js';
import { statusLabel } from '../i18n.js';
import { formatDateTime } from '../utils/formatDate.js';
import styles from './SalesQuotationDetailPage.module.css';

function SealBlock({ quotation, unsealMutation }) {
  const { t, lang, confirm, toastOk, toastErr } = useActionFeedback();

  if (!Boolean(quotation.sealed)) return null;

  const handleUnseal = async () => {
    const ok = await confirm({
      title: t('approvalDetail.seal.confirmUnsealTitle'),
      message: t('approvalDetail.seal.confirmUnsealBody'),
      confirmLabel: t('approvalDetail.seal.unsealNow'),
      tone: 'danger',
    });
    if (!ok) return;

    unsealMutation.mutate(undefined, {
      onSuccess: () => toastOk(t('approvalDetail.seal.unsealedToast')),
      onError: (err) =>
        toastErr(err, [
          'QUOTATION_NOT_FOUND',
          'QUOTATION_NOT_SEALED',
          'QUOTATION_ALREADY_UNSEALED',
          'UNSEAL_NOT_PERMITTED',
        ]),
    });
  };

  const unsealedByLabel = quotation.unsealed_by_name
    ? quotation.unsealed_by_name
    : quotation.unseal_route === 'scheduled'
      ? t('approvalDetail.seal.systemScheduled')
      : t('approvalDetail.seal.unknownActor');

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('approvalDetail.seal.title')}</h2>
      <p className={styles.meta}>
        <strong>{t('approvalDetail.seal.status')}:</strong>{' '}
        {quotation.unsealed_at
          ? t('approvalDetail.seal.statusUnsealed')
          : t('approvalDetail.seal.statusSealed')}
      </p>
      <p className={styles.meta}>
        <strong>{t('approvalDetail.seal.sealedAt')}:</strong>{' '}
        <span className="tabularNums">{formatDateTime(quotation.sealed_at, lang) || '—'}</span>
      </p>
      {quotation.seal_bid_opening_at && (
        <p className={styles.meta}>
          <strong>{t('approvalDetail.seal.bidOpeningAt')}:</strong>{' '}
          <span className="tabularNums">
            {formatDateTime(quotation.seal_bid_opening_at, lang) || '—'}
          </span>
        </p>
      )}
      {quotation.unsealed_at ? (
        <>
          <p className={styles.meta}>
            <strong>{t('approvalDetail.seal.unsealedAt')}:</strong>{' '}
            <span className="tabularNums">
              {formatDateTime(quotation.unsealed_at, lang) || '—'}
            </span>
          </p>
          <p className={styles.meta}>
            <strong>{t('approvalDetail.seal.unsealedBy')}:</strong> {unsealedByLabel}
          </p>
        </>
      ) : quotation.canUnseal ? (
        <button
          type="button"
          className={styles.actionButton}
          onClick={handleUnseal}
          disabled={unsealMutation.isPending}
        >
          {unsealMutation.isPending && <Spinner />}{' '}
          {unsealMutation.isPending
            ? t('approvalDetail.seal.unsealing')
            : t('approvalDetail.seal.unsealNow')}
        </button>
      ) : quotation.sealed_at ? (
        <p className={styles.muted}>{t('error.UNSEAL_NOT_PERMITTED')}</p>
      ) : (
        <p className={styles.muted}>{t('approvalDetail.seal.pendingSendNote')}</p>
      )}
    </section>
  );
}

function RequoteBlock({ quotation, requoteMutation }) {
  const { t, confirm, toastOk, toastErr } = useActionFeedback();

  if (quotation.status !== 'expired') return null;

  const handleRequote = async () => {
    const ok = await confirm({
      title: t('salesQuotationDetail.requote.confirmTitle'),
      message: t('salesQuotationDetail.requote.confirmBody'),
      confirmLabel: t('salesQuotationDetail.requote.button'),
      tone: 'danger',
    });
    if (!ok) return;

    requoteMutation.mutate(undefined, {
      onSuccess: () => toastOk(t('salesQuotationDetail.requote.successToast')),
      onError: (err) =>
        toastErr(err, ['QUOTATION_NOT_REQUOTABLE', 'REQUOTE_NOT_PERMITTED', 'REQUEST_NOT_FOUND']),
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('salesQuotationDetail.requote.title')}</h2>
      <p className={styles.meta}>{t('salesQuotationDetail.requote.hint')}</p>
      {quotation.canRequote ? (
        <button
          type="button"
          className={styles.actionButton}
          onClick={handleRequote}
          disabled={requoteMutation.isPending}
        >
          {requoteMutation.isPending && <Spinner />}{' '}
          {requoteMutation.isPending
            ? t('salesQuotationDetail.requote.requoting')
            : t('salesQuotationDetail.requote.button')}
        </button>
      ) : (
        <p className={styles.muted}>{t('error.REQUOTE_NOT_PERMITTED')}</p>
      )}
    </section>
  );
}

// Payment covering letter — read-only + editable, own quotations only.
// Renders nothing until a payment_requests row exists (not accepted yet, the
// normal pre-acceptance state, same as admin/CLAUDE.md's PaymentSection).
function PaymentLetterBlock({ quotationId }) {
  const { t, toastOk, toastErr } = useActionFeedback();
  const { data: payment, isLoading } = useSalesQuotationPayment(quotationId);
  const letterMutation = useUpdateSalesPaymentLetter(quotationId);

  if (isLoading || !payment) return null;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('paymentLetter.title')}</h2>
      <PaymentLetterForm
        payment={payment}
        isSaving={letterMutation.isPending}
        readOnlyPaid={payment.status === 'paid'}
        onSave={(letterBody) =>
          letterMutation.mutate(letterBody, {
            onSuccess: () => toastOk(t('paymentLetter.savedToast')),
            onError: (err) =>
              toastErr(err, ['PAYMENT_REQUEST_NOT_FOUND', 'PAYMENT_REQUEST_LOCKED']),
          })
        }
      />
    </section>
  );
}

function NoteBlock({ requestId }) {
  const { t, toastOk, toastErr, toast } = useActionFeedback();
  const addNoteMutation = useAddRequestNote(requestId);
  const [body, setBody] = useState('');

  const handleAdd = () => {
    if (!body.trim()) {
      toast.error(t('salesQuotationDetail.note.emptyError'));
      return;
    }
    addNoteMutation.mutate(body.trim(), {
      onSuccess: () => {
        setBody('');
        toastOk(t('salesQuotationDetail.note.successToast'));
      },
      onError: (err) => toastErr(err, ['REQUEST_NOT_FOUND']),
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('salesQuotationDetail.note.title')}</h2>
      <div className={styles.noteForm}>
        <textarea
          className={styles.noteTextarea}
          rows={3}
          maxLength={4000}
          placeholder={t('salesQuotationDetail.note.placeholder')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={addNoteMutation.isPending}
        />
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleAdd}
          disabled={addNoteMutation.isPending}
        >
          {addNoteMutation.isPending && <Spinner />}{' '}
          {addNoteMutation.isPending
            ? t('salesQuotationDetail.note.adding')
            : t('salesQuotationDetail.note.button')}
        </button>
      </div>
    </section>
  );
}

export default function SalesQuotationDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { data: quotation, isLoading, isError, error, refetch } = useSalesQuotationDetail(id);
  const unsealMutation = useUnsealSalesQuotation(id);
  const requoteMutation = useRequoteSalesQuotation(id);

  if (isLoading) {
    return <LoadingState variant="page" />;
  }
  if (isError) {
    return (
      <ErrorState messageId="salesQuotationDetail.errorBody" error={error} onRetry={refetch} />
    );
  }

  const hasPdf = Boolean(quotation.pdf_storage_key);

  return (
    <div className={styles.page}>
      <Link to="/my-quotations" className={styles.back}>
        {t('salesQuotationDetail.back')}
      </Link>

      <p className={styles.readOnlyHint}>{t('allProformas.view.readOnlyHint')}</p>

      <ProformaDocument
        document={quotation}
        lang={lang}
        refNo={quotation.request_ref_no}
        organizationName={
          lang === 'am' && quotation.organization_name_am
            ? quotation.organization_name_am
            : quotation.organization_name
        }
        statusLabel={statusLabel(quotation.status, lang)}
        downloadHref={
          hasPdf
            ? apiClient.url(`/sales/requests/${quotation.request_id}/quotation-pdf`)
            : undefined
        }
        pdfPending={!hasPdf && !['draft', 'pending_approval'].includes(quotation.status)}
      />

      <hr className="hairline" />
      <h2 className={styles.sectionTitle}>{t('salesQuotationDetail.actionsTitle')}</h2>

      <SealBlock quotation={quotation} unsealMutation={unsealMutation} />
      <RequoteBlock quotation={quotation} requoteMutation={requoteMutation} />
      <PaymentLetterBlock quotationId={quotation.id} />
      <NoteBlock requestId={quotation.request_id} />
    </div>
  );
}
